import { RequestAPI } from '~/api/Requests';
import { getLangHeaders } from '~/api/utils';
import { company } from '~/company/Company';
import { DOMAIN, REASON, reportError } from '~/services/telemetry';
import { storage as sessionStorageBackend } from '~/stores/SessionStorage';
import type { UserStore } from '~/stores/UserStore';
import {
  isStaleChunkError,
  pendingUntilReload,
  recoverFromNewBuild,
} from '~/utils/staleChunk';

import { HttpMembershipGateway } from './api/HttpMembershipGateway';
import type {
  MembershipFormErrorSummary,
  MembershipFormIncident,
} from './domain/purchase';
import { adyenCountryCode, adyenLocaleFor } from './payment/adyenConfig';
import type { CardFormFactory } from './payment/CardForm';
import { lazyCardForm } from './payment/lazyCardForm';
import { MembershipMarkers } from './store/MembershipMarkers';

/** The membership gateway over the app's HTTP client; POSTs go through the never-retried `postOnce`. */
export const createGateway = (): HttpMembershipGateway =>
  new HttpMembershipGateway({
    get: (url, data) => RequestAPI.get(url, data, getLangHeaders()),
    postOnce: (url, body) => RequestAPI.postOnce(url, body, getLangHeaders()),
  });

/** Purchase and card change markers in `sessionStorage`, scoped to the signed-in customer. */
export const createMarkers = (userStore: UserStore): MembershipMarkers =>
  new MembershipMarkers({
    storage: sessionStorageBackend,
    getCustomerId: () => userStore.personalData.id,
    now: () => Date.now(),
  });

/**
 * A card form chunk that never arrived reloads the page at once when a deploy
 * is behind it; otherwise the page offers the reload by hand.
 */
const cardFormChunkRecovery = {
  isChunkFailure: isStaleChunkError,
  recover: async (): Promise<void> => {
    const { reloading } = await recoverFromNewBuild();
    if (reloading) {
      await pendingUntilReload<void>();
    }
  },
};

/**
 * The Adyen card form, loaded on first mount, in the customer's language;
 * shared by Join (`?modal=club-join`) and the "Add card" sheet (`?modal=club-card`).
 */
export const createCardFormFactory = (userStore: UserStore): CardFormFactory =>
  lazyCardForm(async () => {
    const { AdyenCardForm } = await import('./payment/AdyenCardForm');
    return () =>
      new AdyenCardForm({
        clientKey: company.envVariables.REACT_APP_ADYEN_CLIENT_KEY,
        countryCode: adyenCountryCode(company.config.countryCode),
        locale: adyenLocaleFor(userStore.lang || company.config.lang),
      });
  }, cardFormChunkRecovery);

/** An `Error` rebuilt from the summary of a card form error, so Sentry groups it like the original. */
const toReportedError = ({
  name,
  message,
  stack,
}: MembershipFormErrorSummary): Error => {
  const error = new Error(message);
  error.name = name;
  if (stack) {
    error.stack = stack;
  }
  return error;
};

/** Sentry report of a card form that failed to load or start; never carries the SDK error itself. */
export const reportFormIncident = (incident: MembershipFormIncident): void => {
  switch (incident.reason) {
    case 'adyenInitFailed':
      reportError(DOMAIN.membership, toReportedError(incident.error), {
        reason: REASON.membership.adyenInitFailed,
        fingerprint: [incident.stage],
        extras: { stage: incident.stage },
      });
      return;
    case 'cardFormChunkFailed':
      reportError(DOMAIN.chunk, incident.error, {
        reason: REASON.chunk.loadFailed,
        level: 'warning',
        extras: { chunk: 'membershipCardForm' },
      });
      return;
    default: {
      const unhandled: never = incident;
      return unhandled;
    }
  }
};
